/**
 * Timezone Utilities
 * 
 * Frontend timezone detection, formatting, and display utilities.
 * Phase 2A - Context Management
 */

/**
 * Detect browser timezone using Intl API
 * Falls back to UTC if detection fails
 * 
 * @returns {string} IANA timezone (e.g., 'America/New_York') or 'UTC'
 */
export const detectBrowserTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (timezone && timezone !== 'undefined') {
      console.log(`✅ Detected timezone: ${timezone}`);
      return timezone;
    }
    
    console.warn('⚠️  Timezone detection returned undefined, falling back to UTC');
    return 'UTC';
    
  } catch (error) {
    console.error('❌ Timezone detection failed:', error);
    return 'UTC'; // Safe fallback
  }
};

/**
 * Format timestamp for display
 * 
 * @param {string} isoString - ISO 8601 timestamp
 * @param {string} timezone - IANA timezone
 * @param {object} options - Custom formatting options
 *   - timeOnly: boolean - Show only time (no date)
 *   - format: 'full' | 'short' | 'date' | 'time' - Predefined formats
 *   - ...other Intl.DateTimeFormat options
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (isoString, timezone = 'UTC', options = {}) => {
  try {
    const date = new Date(isoString);
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', isoString);
      return 'Invalid Date';
    }
    
    // Handle custom format options
    let formatOptions = {};
    
    if (options.timeOnly) {
      // Time only: "5:45 PM"
      formatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone
      };
    } else if (options.format === 'full') {
      // Full format: "Oct 25, 2025 at 5:45 PM PDT"
      formatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        timeZoneName: 'short'
      };
    } else if (options.format === 'short') {
      // Short format: "Oct 25, 5:45 PM"
      formatOptions = {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone
      };
    } else if (options.format === 'date') {
      // Date only: "Oct 25, 2025"
      formatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: timezone
      };
    } else if (options.format === 'time') {
      // Time with seconds: "5:45:30 PM"
      formatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: timezone
      };
    } else {
      // Default format: "Oct 25, 2025, 5:45 PM"
      formatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        ...options
      };
    }
    
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    
  } catch (error) {
    console.error('Timestamp formatting failed:', error);
    return isoString; // Fallback to original
  }
};

/**
 * Get current time in specified timezone
 * 
 * @param {string} timezone - IANA timezone
 * @returns {string} Formatted current time
 */
export const getCurrentTime = (timezone = 'UTC') => {
  try {
    const now = new Date();
    
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    }).format(now);
    
  } catch (error) {
    console.error('Get current time failed:', error);
    return new Date().toLocaleTimeString();
  }
};

/**
 * Get current date in specified timezone
 * 
 * @param {string} timezone - IANA timezone
 * @returns {string} Formatted current date
 */
export const getCurrentDate = (timezone = 'UTC') => {
  try {
    const now = new Date();
    
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    }).format(now);
    
  } catch (error) {
    console.error('Get current date failed:', error);
    return new Date().toLocaleDateString();
  }
};

/**
 * Get timezone offset (e.g., '-07:00', '+05:30')
 * 
 * @param {string} timezone - IANA timezone
 * @returns {string} UTC offset
 */
export const getTimezoneOffset = (timezone = 'UTC') => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    });
    
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');
    
    if (offsetPart && offsetPart.value.startsWith('GMT')) {
      return offsetPart.value.replace('GMT', '');
    }
    
    return '+00:00';
    
  } catch (error) {
    console.error('Get timezone offset failed:', error);
    return '+00:00';
  }
};

/**
 * Format relative time (e.g., "2 minutes ago", "Just now")
 * 
 * @param {string} isoString - ISO 8601 timestamp
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (isoString) => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 30) return 'Just now';
    if (diffSecs < 60) return `${diffSecs} seconds ago`;
    if (diffMins < 60) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
    if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    if (diffDays < 7) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    
    // Fallback to formatted date
    return formatTimestamp(isoString);
    
  } catch (error) {
    console.error('Relative time formatting failed:', error);
    return 'Some time ago';
  }
};

/**
 * Common timezones for dropdown selection
 */
export const COMMON_TIMEZONES = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00' },
  
  // US & Canada
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)', offset: '-06:00' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', offset: '-08:00' },
  { value: 'America/Anchorage', label: 'Alaska Time', offset: '-09:00' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time', offset: '-10:00' },
  
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', offset: '+03:00' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: '+04:00' },
  { value: 'Asia/Kolkata', label: 'India (IST)', offset: '+05:30' },
  { value: 'Asia/Shanghai', label: 'China (CST)', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)', offset: '+09:00' },
  { value: 'Asia/Seoul', label: 'Korea (KST)', offset: '+09:00' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: '+08:00' },
  
  // Australia
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)', offset: '+11:00' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)', offset: '+11:00' },
  { value: 'Australia/Perth', label: 'Perth (AWST)', offset: '+08:00' },
  
  // Others
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', offset: '-03:00' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', offset: '-06:00' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)', offset: '-05:00' },
];

/**
 * Validate timezone string
 * 
 * @param {string} timezone - IANA timezone to validate
 * @returns {boolean} True if valid
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};
